import { Box, Button, ButtonGroup } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { WebStoreContext } from "../../../contexts/WebStoreProvider";
import {
	DebugWebStoreContext,
	LOCAL_STORAGE_DATE_JUMPED
} from "../../contexts/DebugWebStoreProvider";
import ServerTimeControl from "../ServerTimeControl";
import TimeJumpControl from "../TimeJumpControl";
import { useAppDispatch, useAppSelector } from "hooks";
import { debugState, setShowClock } from "redux/slices/debug";

const SHOW_LEGACY_TIME_JUMP_CONTROLS = false;

const TimeTools: React.FC = () => {
	const { nowTime, setNowTime } = useContext(WebStoreContext);
	const dispatch = useAppDispatch();
	const { showClock } = useAppSelector(debugState);
	const { timeJump, resetTimeJump, setServerTime, authToken } =
		useContext(DebugWebStoreContext);
	const [isTimeActionPending, setIsTimeActionPending] = useState(false);

	const runTimeAction = async (action: () => Promise<boolean>) => {
		setIsTimeActionPending(true);
		try {
			const wasSuccessful = await action();
			if (!wasSuccessful) {
				setIsTimeActionPending(false);
			}
			return wasSuccessful;
		} catch (error) {
			setIsTimeActionPending(false);
			throw error;
		}
	};

	useEffect(() => {
		if (!authToken?.value) {
			return;
		}

		const savedTime = localStorage.getItem(LOCAL_STORAGE_DATE_JUMPED);
		if (savedTime) {
			setNowTime(savedTime);
		}
	}, [authToken?.value, setNowTime]);

	const onClickResetDeltaDays = async () => {
		await runTimeAction(async () => {
			const wasSuccessful = await resetTimeJump();
			if (wasSuccessful) {
				try {
					localStorage.removeItem(LOCAL_STORAGE_DATE_JUMPED);
				} catch (error) {
					console.warn("Unable to clear the jumped time", error);
				}
				window.location.reload();
			}
			return wasSuccessful;
		});
	};

	const onSetServerTime = async (now: string) => {
		const wasSuccessful = await runTimeAction(() => setServerTime(now));
		if (wasSuccessful) {
			try {
				localStorage.setItem(LOCAL_STORAGE_DATE_JUMPED, now);
			} catch (error) {
				console.warn("Unable to save the jumped time", error);
			}
			window.location.reload();
		}
	};

	const onClickDeltaChangeTime = async (
		days: number,
		hours?: number,
		minutes?: number
	) => {
		await runTimeAction(async () => {
			const wasSuccessful = await timeJump(nowTime, days, hours, minutes);
			if (wasSuccessful) {
				window.location.reload();
			}
			return wasSuccessful;
		});
	};

	return (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1
				}}
			>
				<h1 style={{ margin: 0 }}>Time</h1>
				<Button onClick={() => dispatch(setShowClock(!showClock))}>
					{showClock ? "Hide Clock" : "Show Clock"}
				</Button>
			</Box>
			<br />
			<ServerTimeControl
				disabled={isTimeActionPending}
				onReset={onClickResetDeltaDays}
				onSet={onSetServerTime}
			/>
			<br />
			<TimeJumpControl
				disabled={isTimeActionPending}
				onTimeJump={onClickDeltaChangeTime}
			/>
			{SHOW_LEGACY_TIME_JUMP_CONTROLS && (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-start",
						gap: "0.5rem",
						width: "100%"
					}}
				>
					<ButtonGroup sx={{ width: "100%", "& .MuiButton-root": { flex: 1 } }}>
						<Button onClick={() => onClickDeltaChangeTime(0, 0, -10)}>
							-10 Minutes
						</Button>
						<Button onClick={() => onClickDeltaChangeTime(0, 0, +10)}>
							+10 Minutes
						</Button>
					</ButtonGroup>
					<ButtonGroup sx={{ width: "100%", "& .MuiButton-root": { flex: 1 } }}>
						<Button onClick={() => onClickDeltaChangeTime(0, -1)}>
							-1 Hour
						</Button>
						<Button onClick={() => onClickDeltaChangeTime(0, +1)}>
							+1 Hour
						</Button>
					</ButtonGroup>
					<ButtonGroup sx={{ width: "100%", "& .MuiButton-root": { flex: 1 } }}>
						<Button onClick={() => onClickDeltaChangeTime(-1)}>-1 Day</Button>
						<Button onClick={() => onClickDeltaChangeTime(+1)}>+1 Day</Button>
					</ButtonGroup>
					<ButtonGroup sx={{ width: "100%", "& .MuiButton-root": { flex: 1 } }}>
						<Button onClick={() => onClickDeltaChangeTime(-7)}>-7 Days</Button>
						<Button onClick={() => onClickDeltaChangeTime(+7)}>+7 Days</Button>
					</ButtonGroup>
					<ButtonGroup sx={{ width: "100%", "& .MuiButton-root": { flex: 1 } }}>
						<Button onClick={() => onClickDeltaChangeTime(-30)}>
							-30 Days
						</Button>
						<Button onClick={() => onClickDeltaChangeTime(+30)}>
							+30 Days
						</Button>
					</ButtonGroup>
				</div>
			)}
			<br />
		</>
	);
};

export default TimeTools;
